import { Folder, Search } from "lucide-react";
import { Input } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ProjectDetails } from "../../types";
import { useRecoilState, useRecoilValue } from "recoil";
import { projectsState, currentProjectState } from "../../recoil/project";
import { getEnvironmentService, getProjectService } from "../../backend";
import { handleSetMixpanelUserGroups } from "../../backend/tracking";
import ProjectCard from "../../components/cards/ProjectCard";
import CardGridSkeleton from "../../components/cards/CardGridSkeleton";
import EmptyState from "../../components/EmptyState";

export default function Projects() {
    const navigate = useNavigate();
    const location = useLocation();
    const [projects, setProjects] = useRecoilState(projectsState);
    const currentProject = useRecoilValue(currentProjectState);
    const [filteredProjects, setFilteredProjects] = useState<ProjectDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);

            // Step 1: Load projects first and show them immediately
            const { data: { projects: projectsList } } = await getProjectService().listProjects();
            const initialProjects = projectsList as ProjectDetails[];

            handleSetMixpanelUserGroups(initialProjects.map(item => item.id));

            // Check if current project exists and redirect if it does
            // But only if user didn't explicitly navigate here (e.g., clicked home icon)
            const showProjectsList = (location.state as any)?.showProjectsList === true;
            if (currentProject?.id && !showProjectsList) {
                const projectExists = initialProjects.find(p => p.id === currentProject.id);
                if (projectExists) {
                    navigate(`/projects/${currentProject.id}/environments`, { replace: true });
                    return;
                }
            }

            // Show projects immediately (environments will load async)
            setProjects(initialProjects);
            setFilteredProjects(initialProjects);
            setLoading(false);

            // Step 2: Load environments asynchronously for each project
            initialProjects.forEach(async (project) => {
                try {
                    const { data: { stages } } = await getEnvironmentService().getStages(project.id);
                    
                    // Update the specific project with its environments
                    setProjects(prev => prev.map(p => 
                        p.id === project.id 
                            ? { ...p, environments: stages }
                            : p
                    ));
                } catch (error) {
                    console.error(`Error loading environments for project ${project.id}:`, error);
                }
            });
        };

        loadData();
    }, []);

    useEffect(() => {
        if (searchText) {
            const filtered = projects.filter(project =>
                project.name.toLowerCase().includes(searchText.toLowerCase()) ||
                project.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                project.environments?.some(e => e.name.toLowerCase().includes(searchText.toLowerCase()))
            );
            setFilteredProjects(filtered);
        } else {
            setFilteredProjects(projects);
        }
    }, [projects, searchText]);

    if (loading) {
        return <CardGridSkeleton variant="project" />;
    }

    return (
        <>
            {projects.length > 0 && (
                <>
                    {projects.length > 6 && (
                        <div className="card-search">
                            <Input
                                placeholder="Search projects..."
                                prefix={<Search size={16} style={{ color: '#9CA3AF' }} />}
                                allowClear
                                size="large"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="cards-grid">
                        {filteredProjects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                    {filteredProjects.length === 0 && searchText && (
                        <EmptyState
                            icon={<Search size={48} />}
                            title="No projects found"
                            description={`No projects match your search "${searchText}". Try a different search term.`}
                        />
                    )}
                </>
            )}

            {projects.length === 0 && (
                <EmptyState
                    icon={<Folder size={48} />}
                    title="Welcome to Microtica!"
                    description="Projects help you organize your infrastructure and applications. Create your first project to get started."
                    actionText="Create Project"
                    actionLink="/projects/create"
                />
            )}
        </>
    );
}
